/**
 * Drawing tool state store (nanostores).
 *
 * M2.T5 + M2.T6 + M2.T9 per IMPLEMENTATION_PLAN §7 M2.T5/T6/T9.
 *
 * Central state for:
 *  - Active tool: "brush" | "eraser" | "fill"
 *  - Brush size: 0 (thin) | 1 (medium) | 2 (thick), maps to pixel widths
 *  - Active color: one of the 6 BrandColor literals
 */

import { atom } from "nanostores";
import type { BrandColor } from "@drawlol/shared";

/** Tool discriminator. "fill" is Free-Draw-only; in-game it is hidden at the UI level. */
export type DrawTool = "brush" | "eraser" | "fill";

/** Brush size index: 0=thin, 1=medium, 2=thick. Matches §13.2.d wire brushSize field. */
export type BrushSizeIndex = 0 | 1 | 2;

/** Logical brush widths in canvas pixels (1600×1200 space). */
export const BRUSH_SIZE_PX: Record<BrushSizeIndex, number> = {
  0: 4, // thin
  1: 8, // medium
  2: 16, // thick
};

/** The 8-color drawing palette (PRD §5.2 + §13.2.d; expanded M2 polish). */
export const PALETTE: BrandColor[] = [
  "#000000", // ink black
  "#e63946", // red
  "#ffd23f", // yellow
  "#4361ee", // blue
  "#7cb518", // green
  "#8b5a2b", // brown
  "#f77f00", // orange (M2 polish: user feedback)
  "#9d4edd", // purple (M2 polish: user feedback)
];

// ---------------------------------------------------------------------------
// Atom stores
// ---------------------------------------------------------------------------

export const $activeTool = atom<DrawTool>("brush");
export const $brushSize = atom<BrushSizeIndex>(1); // default: medium
export const $activeColor = atom<BrandColor>("#000000"); // default: black
